import { authFetchGET, authFetchPOST, FetchStatusCodeError } from '../utils/auth-fetch';
import { VoyageRoute, RoutingJobOptions, VoyageRoutePointFeature, VoyageRouteLineFeature, RouteGeometry, ParametricRollWarning } from '../models/voyage-routing-model';
import { WeatherModel } from '../models/weather-model';
import { getDistance as haversineDist } from 'ol/sphere';
import { deepCopy, findLastIndex } from '../utils/helpers';
import { CoordinatesUtils } from '../utils/coordinates-utils';
import { VoyageImoModel } from '../models/voyage-imo-model';

interface AutoRoutesOptions {
    imo_number: number,
    forecast: string,
    etd: Date,
    eta: Date,
    departure_terminal: string,
    arrival_terminal: string,
    draught_fore: number,
    draught_aft: number,
    reefer_consumption: number,
    maximum_segment_distance: number,
    transverse_metacentric_height: number
}


export interface HydraBestPracticeRoutes
{
  etd: string,
  eta: string,
  departure_terminal: string,
  arrival_terminal: string,
  route: BestPracticeRoute
}

export interface BestPracticeRoute {
    imo_number: number,
    etd: string,
    forecast: string,
    draught_fore: number,
    draught_aft: number,
    reefer_consumption: number,
    route_geometry: Array<VoyageRoutePointFeature>,
    maximum_segment_distance: number,
    transverse_metacentric_height: number,
    totals: {
        fuel_consumption: number,
        distance: number,
        speed_over_ground: number
    },
    errors: string[],
    warnings: string [],
    model_type: string,
    best_practice_route_id: string
    }


const hydraApi = (<any>window)["appenv"].services.hydra;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const env = { ...hydraApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };
const routingSafetyEnv = (<any>window)["appenv"].services.routingSafety;
const routeOptimiserApi = (<any>window)["appenv"].services.routeoptimiserapi;
const newEnv = { ...routeOptimiserApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };

// Hydra cannot parse a normal ISOString with subsecond precision - we need to cut off the "." and everything after
// Bug logged in VSTS: https://maersk-tl-conves.visualstudio.com/Connected%20Vesssel/_workitems/edit/48543
function toHydraDateString(date: Date): string
{
    const isoString = date.toISOString();
    const index = isoString.lastIndexOf(".");
    return isoString.substring(0, index) + "Z";
}

export class HydraRoutingService
{
    static async postCalculationJob(inputRoute: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>
    {
        const route_geometry = this._convertToHydraGeometry(inputRoute.route_geometry);

        // Add ETA to last point
        route_geometry[route_geometry.length-1].properties.eta = toHydraDateString(new Date(options.eta!));

        const payload = {
            imo_number: options.imo || (<any>window)["appenv"].vesselIMONumber,
            forecast: options.forecastVersion || WeatherModel!.latestForecastVersion,
            etd: options.etd ? toHydraDateString(new Date(options.etd)) : toHydraDateString(new Date()),
            reefer_consumption: options.reeferConsumption,
            draught_fore: options.draftFore!, 
            draught_aft: options.draftAft!,
            transverse_metacentric_height: options.gmt,
            route_geometry,
            maximum_segment_distance: options.maxSegmentDistance || this._getMaximumSegmentDistance(route_geometry, options.etd ? options.etd : Date.now(), options.eta!)
        };

        let postResult;
        try
        {
            console.log("Hydra request: ", payload);
            postResult = await authFetchPOST(env.scope, `${env.baseurl}/Vessels/RoutingPowerCalculation`, payload);
        }
        catch (err)
        {
            if (err instanceof FetchStatusCodeError)
            {
                let json: any;
                try
                {
                    // Hydra usually returns a JSON object with a message property
                    json = await err.response.json();
                }
                catch
                {
                    // Apparently not json - just rethrow error
                    throw err;
                }
                throw new Error("Error in route calculation: " + json && json.message ? json.message : err.message);
            }
            else
            {
                throw err;
            }
        }

        if (!postResult || !postResult.route_geometry)
        {
            console.warn('No or unexpected data returned from hydra routing power calculation: ', postResult);
            throw new Error("Error in route calculation: No or unexpected data returned");
        }

        const resultGeometry = this._convertFromHydraGeometry(postResult.route_geometry, inputRoute.route_geometry);

        // Pull ETA from last point
        const endpoint = (<VoyageRoutePointFeature>resultGeometry[resultGeometry.length-1]);
        const eta = endpoint.properties.eta!;
        delete endpoint.properties.eta;

        const resultDuration = new Date(endpoint.properties.timestamp!).getTime() - new Date(postResult.etd).getTime();

        const resultingRoute: VoyageRoute = {
            route_info: {
                eta: eta,
                etd: postResult.etd,
                speed_over_ground: postResult.totals.speed_over_ground,
                co2: -1, // Not available (yet anyway)
                totals: {
                    eca: { time: 0, fuel_cost: 0, vessel_cost: 0, distance: 0, fuel: 0 },
                    non_eca: {
                        time: resultDuration/60*60*1000, // convert to hours
                        fuel_cost: 0, vessel_cost: 0, // Not available
                        distance: postResult.totals.distance/1000, // convert from meters to km
                        fuel: postResult.totals.fuel_consumption
                    }
                },
                rpm: -1, // RPM for entire voyage? TODO - remove this, it is pretty useless
                weather: { // Weather will be added after this
                    forecast_versions: [ ],
                    subscriptions: [],
                    summary: {}
                },
                calculationInput: {
                    forecast_versions: [ payload.forecast ],
                    draftFore: payload.draught_fore,
                    draftAft: payload.draught_aft,
                    gmt: payload.transverse_metacentric_height,
                    etd: payload.etd,
                    eta: eta,
                    weatherLimits: options.weatherLimits!
                },
                hydraErrors: postResult.errors
            },
            route_geometry: resultGeometry,
            routeId: inputRoute.routeId,
        };

        return resultingRoute;
    }

    private static _convertToHydraGeometry(geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>): VoyageRoutePointFeature[]
    {
        const result: VoyageRoutePointFeature[] = [];

        // Skip passed waypoints (if any)
        const lastIsPassedIdx = findLastIndex(geometry, f => f.geometry.type === "Point" && f.properties.isPassed);

        for (let i = lastIsPassedIdx+1; i < geometry.length; i++)
        {
            if (geometry[i].geometry.type === "Point")
            {
                const point = <VoyageRoutePointFeature>geometry[i];
                const resultPoint: VoyageRoutePointFeature = {
                    type: "Feature",
                    geometry: {
                        coordinates: [ point.geometry.coordinates[0], point.geometry.coordinates[1] ],
                        type: "Point"
                    },
                    properties: JSON.parse(JSON.stringify(point.properties))
                };
                if (resultPoint.properties.eta)
                {
                    // Need to convert to hydra-readable format
                    resultPoint.properties.eta = toHydraDateString(new Date(resultPoint.properties.eta));
                }
                result.push(resultPoint);
            }
            else if (geometry[i].geometry.type === "LineString" && result.length > 0)
            {
                // Add leg_type to start point
                if (geometry[i].properties.leg_type)
                {
                    result[result.length-1].properties.leg_type = geometry[i].properties.leg_type === "GreatCircle" ? "great_circle" : "rhumb_line";
                }
                else
                {
                    result[result.length-1].properties.leg_type = this._getLegTypeFromDistance(<VoyageRouteLineFeature>geometry[i]);
                }
            }
        }

        return result;
    }

    private static _convertFromHydraGeometry(points: VoyageRoutePointFeature[], inputRoute: RouteGeometry): RouteGeometry
    {
        const result: RouteGeometry = [];
        
        // Any passed waypoints should be inserted at the beginning
        const lastIsPassedIdx = findLastIndex(inputRoute, f => f.geometry.type === "Point" && f.properties.isPassed);
        if (lastIsPassedIdx !== -1)
        {
            for (let i = 0; i <= lastIsPassedIdx; i++)
            {
                result.push(inputRoute[i]);
            }

            // Add last linestring as well (connecting last passed waypoint with future ones)
            for (let i = lastIsPassedIdx+1; i < inputRoute.length; i++)
            {
                if (inputRoute[i].geometry.type === "LineString")
                {
                    result.push({
                        type: "Feature",
                        geometry: {
                            coordinates: [ <[number,number]>inputRoute[i].geometry.coordinates[0], points[0].geometry.coordinates ],
                            type: "LineString"
                        },
                        properties: inputRoute[i].properties
                    });
                    break;
                }
            }
        }

        for (let i = 0; i < points.length; i++)
        {
            const wp: VoyageRoutePointFeature = {
                type: "Feature",
                geometry: {
                    coordinates: points[i].geometry.coordinates,
                    type: "Point"
                },
                properties: points[i].properties
            };
            result.push(wp);

            if (i !== points.length - 1)
            {
                // Any Informational waypoints should be gathered up and assigned to the LineStrings informationalWaypoints property
                const informationalWaypoints: VoyageRoutePointFeature[] = [];
                while (points[i+1].properties.type === "Informational")
                {
                    informationalWaypoints.push({
                        type: "Feature",
                        geometry: {
                            coordinates: points[i+1].geometry.coordinates,
                            type: "Point"
                        },
                        properties: deepCopy(points[i+1].properties)
                    });
                    delete informationalWaypoints[informationalWaypoints.length-1].properties.leg_type;
                    i++;
                }

                let distance = wp.properties.distance/1000; // convert from meters to km
                let speed_over_ground = wp.properties.speed_over_ground;
                let speed_through_water = wp.properties.speed_through_water;

                if (informationalWaypoints.length > 0)
                {
                    // When there are informational waypoints we need to average speeds and sum up distance for the LineString
                    distance = 0;
                    speed_over_ground = 0;
                    speed_through_water = 0;

                    // Add original start point to informational points as well (since its values are only valid till the next informational point)
                    informationalWaypoints.unshift({
                        type: "Feature",
                        geometry: {
                            coordinates: wp.geometry.coordinates,
                            type: "Point"
                        },
                        properties: deepCopy(wp.properties)
                    });

                    for (const point of informationalWaypoints)
                    {
                        distance += point.properties.distance/1000; // convert from meters to km
                        speed_over_ground += point.properties.speed_over_ground/informationalWaypoints.length;
                        speed_through_water += point.properties.speed_through_water/informationalWaypoints.length;
                    }
                }

                // Add leg and move the relevant properties to the leg
                result.push({
                    type: "Feature",
                    geometry: {
                        coordinates: [ wp.geometry.coordinates, points[i+1].geometry.coordinates ],
                        type: "LineString"
                    },
                    properties: {
                        leg_type: wp.properties.leg_type === "great_circle" ? "GreatCircle" : "Rhumbline",
                        distance: distance,
                        course: wp.properties.course_over_ground,
                        speed_over_ground: speed_over_ground,
                        speed_through_water: speed_through_water,
                        propulsion_power: wp.properties.propulsion_power,
                        propulsion_power_lower: wp.properties.propulsion_power_lower,
                        propulsion_power_upper: wp.properties.propulsion_power_upper,
                        main_engine_rpm: wp.properties.main_engine_rpm,
                        main_engine_rpm_lower: wp.properties.main_engine_rpm_lower,
                        main_engine_rpm_upper: wp.properties.main_engine_rpm_upper,
                        informationalWaypoints: informationalWaypoints.length > 0 ? informationalWaypoints : undefined
                    }
                });

                delete wp.properties.leg_type;
                delete wp.properties.distance;
                delete wp.properties.course_over_ground;
                delete wp.properties.speed_over_ground;
                delete wp.properties.speed_through_water;
                delete wp.properties.propulsion_power;
                delete wp.properties.main_engine_rpm;
            }
        }

        this._processParametricRoll(result);
        const res = this._addMinMaxSpeed(result);
        return res;
    }

    private static _addMinMaxSpeed(result:RouteGeometry):RouteGeometry {
        let lastHardPoint: VoyageRoutePointFeature | null = null;

        return result.map(el => {
            if(el.properties.type === 'Hard') {
                lastHardPoint = el as VoyageRoutePointFeature;
            }

            if((el.properties.type === 'Navigational' || el.properties.type === 'Informational')) {           
                if(!el.properties.speed_maximum) {
                    el.properties = {...el.properties, speed_maximum: lastHardPoint?.properties.speed_maximum} 
                } 
                if(!el.properties.speed_minimum ) {
                    el.properties = {...el.properties, speed_minimum: lastHardPoint?.properties.speed_minimum} 
                } 
                
                if(!el.properties.min_depth) {
                    el.properties = {...el.properties, min_depth: lastHardPoint?.properties.min_depth} 
                }
            }

            return el
        })
    }

    private static _getLegTypeFromDistance(lineString: VoyageRouteLineFeature): string
    {
        const gcMinDistance = 300*1852; // Distances less than 300 nm default to rhumb line

        if (haversineDist(lineString.geometry.coordinates[0], lineString.geometry.coordinates[1]) < gcMinDistance)
        {
            return "rhumb_line";
        }
        else
        {
            return "great_circle";
        }
    }

    // Calculate desired maximum segment distance in km
    private static _getMaximumSegmentDistance(hydraGeometry: VoyageRoutePointFeature[], etd: number, eta: number): number
    {
        // Try to get informational waypoints around every 2 hours of voyage time
        // Clamp result to be in the range between 10 and 100 nm (18.52 and 185.2 km)

        let totalDist = 0; // in km
        let prevPoint: VoyageRoutePointFeature|null = null;
        for (const point of hydraGeometry)
        {
            if (prevPoint)
            {
                const dist = CoordinatesUtils.calcDistance(prevPoint.geometry.coordinates, point.geometry.coordinates);
                if (prevPoint.properties.leg_type == "great_circle")
                {
                    totalDist += dist.gc*1.852;
                }
                else
                {
                    totalDist += dist.rl*1.852;
                }
            }
            prevPoint = point;
        }

        let totalHours = (eta - etd)/(1000*60*60);

        let maxSegmentDist = totalDist*2/totalHours;

        // Clamp to accepted range and round up
        return Math.ceil(Math.min(Math.max(maxSegmentDist, 18.52), 185.2));
    }

    private static _processParametricRoll(routeGeometry: RouteGeometry): void
    {
        const lastIsPassed = findLastIndex(routeGeometry, f => f.geometry.type === "Point" && f.properties.isPassed);
        const fromIndex = lastIsPassed !== -1 ? lastIsPassed + 1 : 1;

        for (let i = fromIndex; i < routeGeometry.length-1; i++)
        {
            if (routeGeometry[i].geometry.type == "LineString")
            {
                const line = <VoyageRouteLineFeature>routeGeometry[i];
                const infoWPs = line.properties.informationalWaypoints;
    
                if (!infoWPs)
                {
                    // No informational waypoints, use start/end points
                    if (routeGeometry[i-1].properties.is_high_risk_roll)
                    {
                        this._addParametricRollWarning(line, {
                            coordinates: [
                                (<VoyageRoutePointFeature>routeGeometry[i-1]).geometry.coordinates,
                                (<VoyageRoutePointFeature>routeGeometry[i+1]).geometry.coordinates
                            ],
                            timeStart: routeGeometry[i-1].properties.timestamp,
                            timeEnd: routeGeometry[i+1].properties.timestamp,
                            roll_risk_info: routeGeometry[i-1].properties.roll_risk_info
                        });
                    }
                }
                else
                {
                    // Gather info from informational waypoints
                    let startPoint: VoyageRoutePointFeature|null = null;
                    for (const infoWaypoint of infoWPs)
                    {
                        if (startPoint)
                        {
                            if (!infoWaypoint.properties.is_high_risk_roll)
                            {
                                this._addParametricRollWarning(line, {
                                    coordinates: [
                                        startPoint.geometry.coordinates,
                                        infoWaypoint.geometry.coordinates
                                    ],
                                    timeStart: startPoint.properties.timestamp!,
                                    timeEnd: infoWaypoint.properties.timestamp!,
                                    roll_risk_info: startPoint.properties.roll_risk_info
                                });
                                startPoint = null;
                            }
                        }
                        else if (infoWaypoint.properties.is_high_risk_roll)
                        {
                            startPoint = infoWaypoint;
                        }
                    }
    
                    if (startPoint)
                    {
                        this._addParametricRollWarning(line, {
                            coordinates: [
                                startPoint.geometry.coordinates,
                                (<VoyageRoutePointFeature>routeGeometry[i+1]).geometry.coordinates
                            ],
                            timeStart: startPoint.properties.timestamp!,
                            timeEnd: routeGeometry[i+1].properties.timestamp,
                            roll_risk_info: startPoint.properties.roll_risk_info
                        });
                    }
                }
            }
        }
    }

    private static _addParametricRollWarning(line: VoyageRouteLineFeature, warning: ParametricRollWarning): void
    {
        if (!line.properties.warnings || !line.properties.warnings.parametricRoll)
        {
            line.properties.warnings = {
                weather: { avoid:[], warn: [] },
                parametricRoll: []
            };
        }

        line.properties.warnings.parametricRoll!.push(warning);
    }

    static async getBestPracticeRoute(options: AutoRoutesOptions) {
        try {
            return await authFetchPOST(env.scope, `${env.baseurl}/Vessels/BestPracticeRoutes`, options);
        } catch(err) {
            return null;
        }
    }

    static async checkIfKrugerModelIsAvailable(): Promise<boolean> {
        try {
            const ImoNumber = isVesselOnCloud ? VoyageImoModel?.imo : (<any>window)["appenv"].vesselIMONumber;
            const response = isVesselOnCloud ? await authFetchGET(env.scope, `${newEnv.baseurl}/v1/modelname/${ImoNumber}`) : await authFetchGET(routingSafetyEnv.scope, `${routingSafetyEnv.baseurl}/critical-roll-polar-plot-model-name/${ImoNumber}`);
            if(response?.roll_angle_model || response?.critical_roll_model) {
                return (response.roll_angle_model === "MLModel" || response.critical_roll_model === "MLModel");
            }
            return false;
        } catch (error) {
            console.error("Error checking if Kruger model is available: ", error);
            return false;
        }
    }
}
